import React from 'react'

import {
  Modal,
  ModalCloseButton,
  ModalOverlay,
  ScrollableModalContent
} from 'shared/components/common/modal'
import { t } from 'src/translations'
import {
  ModifierGroup,
  Modifier
} from 'public/components/online_ordering/types'
import { useModifierContext } from 'public/components/online_ordering/ModifierContext'
import { DoneIcon } from '@toasttab/buffet-pui-icons'

import { ModifierGroupSelections } from './ModifierGroupSelections'
import { getModifierGroupSubtitle } from './getModifierGroupSubtitle'
import { normalizeTextForScreenReader } from 'src/shared/utils/accessibilityTextUtils'

type ModifierOverflowModalProps = {
  isOpen: boolean
  onClose: () => void
  group: ModifierGroup
  overflowModifiers: Modifier[]
  inputType: 'radio' | 'checkbox'
}

export const ModifierOverflowModal = ({
  isOpen,
  onClose,
  group,
  overflowModifiers,
  inputType
}: ModifierOverflowModalProps) => {
  const { guid, name, minSelections, maxSelections } = group
  const required = Boolean(minSelections && minSelections > 0)
  const { isValid } = useModifierContext()
  const valid = isValid(guid)

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      testId='pdp-modifiers-overflow-modal'
    >
      <ModalOverlay />
      <ScrollableModalContent
        ariaLabelledBy={`pdp-modifiers-overflow-title-${guid}`}
        contentClassName='pdpModifierOverflowModalContent'
        wrapperClassName='pdpModifierOverflowModalWrapper'
      >
        <div className='pdpModifierOverflowModal'>
          <div className='pdpModifierOverflowModalTitleBar'>
            <h2
              id={`pdp-modifiers-overflow-title-${guid}`}
              className='pdpModifierOverflowModalTitle'
              aria-label={
                name
                  ? normalizeTextForScreenReader(name)
                  : t('pdp-modifiers.overflow-modal-title-fallback')
              }
            >
              <span>
                {name || t('pdp-modifiers.overflow-modal-title-fallback')}
              </span>
            </h2>
            <ModalCloseButton className='close' />
          </div>
          <div className='pdpModifierOverflowModalBody'>
            <div className='pdpModifierOverflowModalCard'>
              <div className='pdpModifierOverflowModalCardHeader'>
                <div className='pdpModifierOverflowModalCardTitleRow'>
                  <h3
                    className='pdpModifierOverflowModalCardTitle'
                    aria-label={
                      name
                        ? normalizeTextForScreenReader(name)
                        : t('pdp-modifiers.overflow-modal-title-fallback')
                    }
                  >
                    <span>
                      {name || t('pdp-modifiers.overflow-modal-title-fallback')}
                    </span>
                  </h3>
                  {required && (
                    <div
                      className={`pdpModifierOverflowModalRequiredBadge ${
                        valid ? 'valid' : ''
                      }`}
                    >
                      {valid && <DoneIcon accessibility='decorative' />}
                      {t('selection-list.required')}
                    </div>
                  )}
                </div>
                <div className='pdpModifierOverflowModalCardSubtitle'>
                  {getModifierGroupSubtitle(minSelections, maxSelections)}
                </div>
              </div>
              <div
                className='selections'
                role={inputType === 'radio' ? 'radiogroup' : 'group'}
                data-testid='pdp-modifiers-overflow-selections'
              >
                <ModifierGroupSelections
                  groups={overflowModifiers}
                  group={group}
                  inputType={inputType}
                  firstModifierGroup={false}
                />
              </div>
            </div>
          </div>
          <div className='pdpModifierOverflowModalFooter'>
            <button
              type='button'
              className='pdpModifierOverflowModalConfirmButton'
              onClick={onClose}
              data-testid='pdp-modifiers-overflow-confirm'
            >
              {t('pdp-modifiers.confirm-selection')}
            </button>
          </div>
        </div>
      </ScrollableModalContent>
    </Modal>
  )
}
